import { Flex,Select,Slider,Tooltip } from "antd";
import React,{ useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../../config/styles/theme.less";
import { calculateZoneArea } from "../../../../maps/utils/autozones";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { useNewTrialState } from "../../../new-trail/hooks/NewTrialStateProvider";
import useFormHandling from "../../../new-trail/hooks/useFormHandling";
import { AutozoneColorPalette,AUTOZONE_REQUEST_STATUS } from "../../types";
import {
  getAccessibilityPaletteValue,
  getColorForNDVIValue,
  getDefaultColorPaletteValue
} from "../../utils/autozoneColors";
import { AutozoneColorPaletteOptions } from "../../utils/constants";
import { DrawerAutozoneError } from "./DrawerAutozoneError";
import DrawerSkeleton from "./DrawerSkeleton";
import ModalInfo from "./ModalInfo";
import ZoneInfoModal from "./ZoneInfoModal";

function getVariabilityTagType(value: number): any {
  value = Math.max(0, Math.min(1, value));
  if (value >= 0.65) {
    return "HIGH";
  } else if (value >= 0.6) {
    return "MEDIUM";
  } else {
    return "LOW";
  }
}

const InfoIcon: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <svg
      onClick={onClick}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ cursor: "pointer" }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.83333 0C2.61333 0 0 2.61333 0 5.83333C0 9.05333 2.61333 11.6667 5.83333 11.6667C9.05333 11.6667 11.6667 9.05333 11.6667 5.83333C11.6667 2.61333 9.05333 0 5.83333 0ZM5.25 2.91667V4.08333H6.41667V2.91667H5.25ZM5.25 5.25V8.75H6.41667V5.25H5.25ZM1.16667 5.83333C1.16667 8.40583 3.26083 10.5 5.83333 10.5C8.40583 10.5 10.5 8.40583 10.5 5.83333C10.5 3.26083 8.40583 1.16667 5.83333 1.16667C3.26083 1.16667 1.16667 3.26083 1.16667 5.83333Z"
        fill="#A3A9B9"
      />
    </svg>
  );
};

interface DrawerDataProps {
  fieldId: string;
  onClose?: () => void;
}

const DrawerData: React.FC<DrawerDataProps> = ({ fieldId, onClose }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const {
    autozonesData,
    autozoneRequestStatus,
    autozoneOpacity,
    setAutozoneOpacity,
    setAutozoneColorPalette,
    autozoneColorPalette,
  } = useNewTrialState() || {};
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    type: "",
  });
  const productivityZonesData = autozonesData?.productivity_zones;
  const autozoneVariabilityData = autozonesData?.variability_index;
  const variabilityData = autozoneVariabilityData?.results[0]?.predictions[0];

  const { fieldData } = useFormHandling() || {};
  const data =
    productivityZonesData?.results[0]?.predictions[0]?.productivity_zones
      ?.features;
  const handleCancel = (): void => {
    setIsModalOpen(false);
  };

  function toggleModal(isOpen = false, type = ""): void {
    setInfoModal({ isOpen, type });
  }

  let selectedFieldData: any = null;
  if (Array.isArray(fieldData) && fieldId) {
    const selectedField: any = fieldData.find(
      (field: any) => field.id === fieldId
    );

    if (selectedField) {
      // const zone1 = calculateZoneArea(selectedField, data[0].geometry as any);

      selectedFieldData = selectedField;
    }
  }
  const handleColorPaletteChange = (value: AutozoneColorPalette): void => {
    setAutozoneColorPalette(value);
  };

  if (autozoneRequestStatus === AUTOZONE_REQUEST_STATUS.LOADING) {
    return <DrawerSkeleton />;
  }

  if (autozoneRequestStatus === AUTOZONE_REQUEST_STATUS.ERROR) {
    return <DrawerAutozoneError onClose={onClose} />;
  }

  return (
    <div>
      {/* <section className="zone-info-section">
        <div className="text-md text-semibold">
          {formatTranslation(t("autozones.drawer.title.generated"))}{" "}
          {data?.length} {formatTranslation(t("autozones.drawer.title.zones"))}
        </div>
        <div className="text-sm">
          {formatTranslation(t("autozones.drawer.at"))}{" "}
          {new Date(productivityZonesData?.metadata.result_time * 1000)
            .toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
            .replace(":", "h")}
        </div>
      </section> */}
      <div className="drawer-data">
        <ZoneInfoModal
          isModalOpen={isModalOpen}
          handleModalClose={handleCancel}
        />
        <ModalInfo
          isOpen={infoModal.isOpen}
          onClose={toggleModal}
          type={infoModal.type}
        />

        <div className="variability-index-table">
          <div className="abs-line">
            <svg
              width="11"
              height="70"
              viewBox="0 0 11 70"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 0.5C1 0.223858 0.776142 0 0.5 0C0.223858 0 0 0.223858 0 0.5H0.5H1ZM4.83333 27.5C4.83333 28.9728 6.02724 30.1667 7.5 30.1667C8.97276 30.1667 10.1667 28.9728 10.1667 27.5C10.1667 26.0272 8.97276 24.8333 7.5 24.8333C6.02724 24.8333 4.83333 26.0272 4.83333 27.5ZM0 1.54167C0 1.81781 0.223858 2.04167 0.5 2.04167C0.776142 2.04167 1 1.81781 1 1.54167H0.5H0ZM1 3.625C1 3.34886 0.776142 3.125 0.5 3.125C0.223858 3.125 0 3.34886 0 3.625H0.5H1ZM0 5.70833C0 5.98448 0.223858 6.20833 0.5 6.20833C0.776142 6.20833 1 5.98448 1 5.70833H0.5H0ZM1 7.79167C1 7.51552 0.776142 7.29167 0.5 7.29167C0.223858 7.29167 0 7.51552 0 7.79167H0.5H1ZM0 9.875C0 10.1511 0.223858 10.375 0.5 10.375C0.776142 10.375 1 10.1511 1 9.875H0.5H0ZM1 11.9583C1 11.6822 0.776142 11.4583 0.5 11.4583C0.223858 11.4583 0 11.6822 0 11.9583H0.5H1ZM0 14.0417C0 14.3178 0.223858 14.5417 0.5 14.5417C0.776142 14.5417 1 14.3178 1 14.0417H0.5H0ZM1 16.125C1 15.8489 0.776142 15.625 0.5 15.625C0.223858 15.625 0 15.8489 0 16.125H0.5H1ZM0 18.2083C0 18.4845 0.223858 18.7083 0.5 18.7083C0.776142 18.7083 1 18.4845 1 18.2083H0.5H0ZM1 20.2917C1 20.0155 0.776142 19.7917 0.5 19.7917C0.223858 19.7917 0 20.0155 0 20.2917H0.5H1ZM0 22.375C0 22.6511 0.223858 22.875 0.5 22.875C0.776142 22.875 1 22.6511 1 22.375H0.5H0ZM1 24.4583C1 24.1822 0.776142 23.9583 0.5 23.9583C0.223858 23.9583 0 24.1822 0 24.4583H0.5H1ZM0.189898 26.4571C0.295676 26.7122 0.588208 26.8332 0.843288 26.7274C1.09837 26.6217 1.2194 26.3291 1.11362 26.074L0.651761 26.2656L0.189898 26.4571ZM1.92595 26.8864C1.67087 26.7806 1.37834 26.9016 1.27256 27.1567C1.16678 27.4118 1.28782 27.7043 1.5429 27.8101L1.73442 27.3482L1.92595 26.8864ZM3.75 28C4.02614 28 4.25 27.7761 4.25 27.5C4.25 27.2239 4.02614 27 3.75 27V27.5V28ZM6.25 27C5.97386 27 5.75 27.2239 5.75 27.5C5.75 27.7761 5.97386 28 6.25 28V27.5V27ZM0.5 0.5H0V1.54167H0.5H1V0.5H0.5ZM0.5 3.625H0V5.70833H0.5H1V3.625H0.5ZM0.5 7.79167H0V9.875H0.5H1V7.79167H0.5ZM0.5 11.9583H0V14.0417H0.5H1V11.9583H0.5ZM0.5 16.125H0V18.2083H0.5H1V16.125H0.5ZM0.5 20.2917H0V22.375H0.5H1V20.2917H0.5ZM0.5 24.4583H0V25.5H0.5H1V24.4583H0.5ZM0.5 25.5H0C0 25.8381 0.0673745 26.1616 0.189898 26.4571L0.651761 26.2656L1.11362 26.074C1.04057 25.8979 1 25.7043 1 25.5H0.5ZM1.73442 27.3482L1.5429 27.8101C1.83836 27.9326 2.16194 28 2.5 28V27.5V27C2.29569 27 2.10212 26.9594 1.92595 26.8864L1.73442 27.3482ZM2.5 27.5V28H3.75V27.5V27H2.5V27.5ZM6.25 27.5V28H7.5V27.5V27H6.25V27.5Z"
                fill="#DFE2E7"
              />
              <path
                d="M1 24.5C1 24.2239 0.776142 24 0.5 24C0.223858 24 0 24.2239 0 24.5H0.5H1ZM4.83333 66.5C4.83333 67.9728 6.02724 69.1667 7.5 69.1667C8.97276 69.1667 10.1667 67.9728 10.1667 66.5C10.1667 65.0272 8.97276 63.8333 7.5 63.8333C6.02724 63.8333 4.83333 65.0272 4.83333 66.5ZM0 25.5C0 25.7761 0.223858 26 0.5 26C0.776142 26 1 25.7761 1 25.5H0.5H0ZM1 27.5C1 27.2239 0.776142 27 0.5 27C0.223858 27 0 27.2239 0 27.5H0.5H1ZM0 29.5C0 29.7761 0.223858 30 0.5 30C0.776142 30 1 29.7761 1 29.5H0.5H0ZM1 31.5C1 31.2239 0.776142 31 0.5 31C0.223858 31 0 31.2239 0 31.5H0.5H1ZM0 33.5C0 33.7761 0.223858 34 0.5 34C0.776142 34 1 33.7761 1 33.5H0.5H0ZM1 35.5C1 35.2239 0.776142 35 0.5 35C0.223858 35 0 35.2239 0 35.5H0.5H1ZM0 37.5C0 37.7761 0.223858 38 0.5 38C0.776142 38 1 37.7761 1 37.5H0.5H0ZM1 39.5C1 39.2239 0.776142 39 0.5 39C0.223858 39 0 39.2239 0 39.5H0.5H1ZM0 41.5C0 41.7761 0.223858 42 0.5 42C0.776142 42 1 41.7761 1 41.5H0.5H0ZM1 43.5C1 43.2239 0.776142 43 0.5 43C0.223858 43 0 43.2239 0 43.5H0.5H1ZM0 45.5C0 45.7761 0.223858 46 0.5 46C0.776142 46 1 45.7761 1 45.5H0.5H0ZM1 47.5C1 47.2239 0.776142 47 0.5 47C0.223858 47 0 47.2239 0 47.5H0.5H1ZM0 49.5C0 49.7761 0.223858 50 0.5 50C0.776142 50 1 49.7761 1 49.5H0.5H0ZM1 51.5C1 51.2239 0.776142 51 0.5 51C0.223858 51 0 51.2239 0 51.5H0.5H1ZM0 53.5C0 53.7761 0.223858 54 0.5 54C0.776142 54 1 53.7761 1 53.5H0.5H0ZM1 55.5C1 55.2239 0.776142 55 0.5 55C0.223858 55 0 55.2239 0 55.5H0.5H1ZM0 57.5C0 57.7761 0.223858 58 0.5 58C0.776142 58 1 57.7761 1 57.5H0.5H0ZM1 59.5C1 59.2239 0.776142 59 0.5 59C0.223858 59 0 59.2239 0 59.5H0.5H1ZM0 61.5C0 61.7761 0.223858 62 0.5 62C0.776142 62 1 61.7761 1 61.5H0.5H0ZM1 63.5C1 63.2239 0.776142 63 0.5 63C0.223858 63 0 63.2239 0 63.5H0.5H1ZM0.189898 65.4571C0.295676 65.7122 0.588208 65.8332 0.843288 65.7274C1.09837 65.6217 1.2194 65.3291 1.11362 65.074L0.651761 65.2656L0.189898 65.4571ZM1.92595 65.8864C1.67087 65.7806 1.37834 65.9016 1.27256 66.1567C1.16678 66.4118 1.28782 66.7043 1.5429 66.8101L1.73442 66.3482L1.92595 65.8864ZM3.75 67C4.02614 67 4.25 66.7761 4.25 66.5C4.25 66.2239 4.02614 66 3.75 66V66.5V67ZM6.25 66C5.97386 66 5.75 66.2239 5.75 66.5C5.75 66.7761 5.97386 67 6.25 67V66.5V66ZM0.5 24.5H0V25.5H0.5H1V24.5H0.5ZM0.5 27.5H0V29.5H0.5H1V27.5H0.5ZM0.5 31.5H0V33.5H0.5H1V31.5H0.5ZM0.5 35.5H0V37.5H0.5H1V35.5H0.5ZM0.5 39.5H0V41.5H0.5H1V39.5H0.5ZM0.5 43.5H0V45.5H0.5H1V43.5H0.5ZM0.5 47.5H0V49.5H0.5H1V47.5H0.5ZM0.5 51.5H0V53.5H0.5H1V51.5H0.5ZM0.5 55.5H0V57.5H0.5H1V55.5H0.5ZM0.5 59.5H0V61.5H0.5H1V59.5H0.5ZM0.5 63.5H0V64.5H0.5H1V63.5H0.5ZM0.5 64.5H0C0 64.8381 0.0673745 65.1616 0.189898 65.4571L0.651761 65.2656L1.11362 65.074C1.04057 64.8979 1 64.7043 1 64.5H0.5ZM1.73442 66.3482L1.5429 66.8101C1.83836 66.9326 2.16194 67 2.5 67V66.5V66C2.29569 66 2.10212 65.9594 1.92595 65.8864L1.73442 66.3482ZM2.5 66.5V67H3.75V66.5V66H2.5V66.5ZM6.25 66.5V67H7.5V66.5V66H6.25V66.5Z"
                fill="#DFE2E7"
              />
            </svg>
          </div>

          <table width={"100%"}>
            <tr>
              <td className="header-td">
                <strong>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="17"
                    viewBox="0 0 15 17"
                    fill="none"
                  >
                    <path
                      d="M0.0600586 13.8811L6 7.94112V10.0636L1.11756 14.9386C0.862559 14.8711 0.630059 14.7361 0.442559 14.5561C0.262559 14.3686 0.127559 14.1361 0.0600586 13.8811Z"
                      fill="#696F88"
                    />
                    <path
                      d="M6 2.16002L0 8.15997V10.2825L6 4.28252V2.16002Z"
                      fill="#696F88"
                    />
                    <path
                      d="M1.5 1.5C0.675 1.5 0 2.175 0 3V4.5L3 1.5H1.5Z"
                      fill="#696F88"
                    />
                    <path
                      d="M4.71753 15L6 15V13.7176L4.71753 15Z"
                      fill="#696F88"
                    />
                    <path d="M6 0H7.5V16.5H6V0Z" fill="#696F88" />
                    <path
                      d="M9 4.5V3L12 3V1.5H13.5V3L15 3V4.5H13.5V12H15V13.5H13.5V15H12V13.5H9V12H12V4.5L9 4.5Z"
                      fill="#696F88"
                    />
                  </svg>
                </strong>
                <strong>
                  {" "}
                  {formatTranslation(t("autozones.drawer.index.variability"))}
                </strong>
                <InfoIcon
                  onClick={() => {
                    toggleModal(true, "variability");
                  }}
                />
              </td>

              <td>
                <span className="badge fs-12">
                  {getVariabilityTagType(variabilityData?.vxc)}
                </span>
              </td>
              <td> {variabilityData?.vxc.toFixed(2)}</td>
            </tr>
            <tr>
              <td className="header-td abs-padding">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.66 0L0 6.66V8.7825L8.7825 0H6.66ZM12.3826 0.0585938L0.0600586 12.3811C0.127559 12.6361 0.262559 12.8686 0.442559 13.0561C0.630059 13.2361 0.862559 13.3711 1.11756 13.4386L13.4476 1.11609C13.3051 0.598594 12.9001 0.193594 12.3826 0.0585938ZM0 1.5C0 0.675 0.675 0 1.5 0H3L0 3V1.5ZM12 13.5C12.4125 13.5 12.7875 13.335 13.0575 13.0575C13.335 12.7875 13.5 12.4125 13.5 12V10.5L10.5 13.5H12ZM6.84 13.5H4.7175L13.5 4.7175V6.84L6.84 13.5Z"
                    fill="#696F88"
                  />
                </svg>
                {formatTranslation(t("autozones.drawer.index.heterogeneity"))}
                <InfoIcon
                  onClick={() => {
                    toggleModal(true, "heterogeneity");
                  }}
                />
              </td>
              <td style={{ paddingLeft: "5px" }}>
                <span className="fs-12">
                  {getVariabilityTagType(variabilityData?.variability)}
                </span>
              </td>
              <td>{variabilityData?.variability.toFixed(2)}</td>
            </tr>
            <tr>
              <td className="header-td abs-padding">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.25 7.5C13.32 7.5 15 9.18 15 11.25C15 13.32 13.32 15 11.25 15C9.18 15 7.5 13.32 7.5 11.25C7.5 9.18 9.18 7.5 11.25 7.5ZM3 0V1.5H10.5V0H12V1.5H13.5V3H12V6.05371C11.7551 6.01868 11.5046 6 11.25 6C10.9953 6 10.745 6.01867 10.5 6.05371V3H3V10.5H6.05273C6.0177 10.7449 6 10.9954 6 11.25C6 11.5046 6.01771 11.7551 6.05273 12H3V13.5H1.5V12H0V10.5H1.5V3H0V1.5H1.5V0H3ZM10.875 9V11.4004L12.4873 13.0127L13.0127 12.4805L11.625 11.0928V9H10.875Z"
                    fill="#696F88"
                  />
                </svg>

                {formatTranslation(t("autozones.drawer.index.consistency"))}
                <InfoIcon
                  onClick={() => {
                    toggleModal(true, "consistency");
                  }}
                />
              </td>
              <td style={{ paddingLeft: "5px" }}>
                <span className=" fs-12">
                  {getVariabilityTagType(variabilityData?.consistency)}
                </span>
              </td>
              <td> {variabilityData?.consistency.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        <section
          className="zone-info-section"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="text-md text-semibold">
            {data?.length}{" "}
            {formatTranslation(t("autozones.drawer.title.zones"))}{" "}
            {formatTranslation(t("autozones.drawer.title.generated"))}
          </div>
          <div className="text-sm">
            {/* {formatTranslation(t("autozones.drawer.at"))}{" "} */}
            {new Date(productivityZonesData?.metadata.result_time * 1000)
              .toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
              .replace(",", "")
              .replace(":", "h")}
          </div>
        </section>
        <div className="zone-table">
          <table width={"100%"}>
            <tr>
              <th>
                {formatTranslation(t("autozones.drawer.title.zones.upper"))}
              </th>
              <th colSpan={2}>
                {formatTranslation(t("autozones.drawer.title.area"))}
              </th>
              <th>
                <div className="prod-diff-header">
                  {formatTranslation(t("autozones.drawer.title.prod-diff"))}
                  <Tooltip
                    title={`Productivity difference shows the relative productivity of this zone compared to the field average, based on NDVI and adjustment factors.
Positive = higher yield potential, 
Negative = lower`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.99935 1.1665C3.77935 1.1665 1.16602 3.77984 1.16602 6.99984C1.16602 10.2198 3.77935 12.8332 6.99935 12.8332C10.2193 12.8332 12.8327 10.2198 12.8327 6.99984C12.8327 3.77984 10.2193 1.1665 6.99935 1.1665ZM6.41602 4.08301V5.24967H7.58268V4.08301H6.41602ZM6.41602 6.4165V9.9165H7.58268V6.4165H6.41602ZM2.33203 6.99992C2.33203 9.57242 4.4262 11.6666 6.9987 11.6666C9.5712 11.6666 11.6654 9.57242 11.6654 6.99992C11.6654 4.42742 9.5712 2.33325 6.9987 2.33325C4.4262 2.33325 2.33203 4.42742 2.33203 6.99992Z"
                        fill="#C2C7D0"
                      />
                    </svg>
                  </Tooltip>
                </div>
              </th>
              <th>{formatTranslation(t("autozones.drawer.title.ndvi"))}</th>
            </tr>
            <tr>
              <th></th>
              <td className="color__grey">%</td>
              <td className="color__grey">ha</td>
              <th></th>
              <th></th>
            </tr>
            {data
              ?.slice() // create a shallow copy to avoid mutating original data
              .sort(
                (a, b) =>
                  a.properties.percentiles.p50 - b.properties.percentiles.p50
              )
              .map((zone: any, index) => {
                const { zonePercentage = 0, zoneArea = 0 } =
                  calculateZoneArea(selectedFieldData, zone) || {};
                const prodDiff =
                  (zone?.properties?.productivity_adjustment_factor || 0) * 100;
                return (
                  <tr key={index}>
                    <td>
                      <div className="zone-container">
                        <div
                          className="zone-color-circle"
                          style={{
                            backgroundColor:
                              autozoneColorPalette ===
                              AutozoneColorPalette.DEFAULT
                                ? getDefaultColorPaletteValue(
                                    index,
                                    data.length
                                  )
                                : autozoneColorPalette ===
                                  AutozoneColorPalette.ACCESSIBILITY
                                ? getAccessibilityPaletteValue(
                                    index,
                                    data.length
                                  )
                                : getColorForNDVIValue(
                                    zone.properties.percentiles.p50
                                  ),
                          }}
                        ></div>
                        <span>{index + 1}</span>
                      </div>
                    </td>
                    <td>{zonePercentage.toFixed(2)}</td>
                    <td className="color__grey">{zoneArea.toFixed(2)}</td>
                    <td>
                      {prodDiff > 0
                        ? `+${prodDiff.toFixed(2)}`
                        : prodDiff.toFixed(2)}
                      %
                    </td>
                    <td>{zone.properties.percentiles.p50.toFixed(2)}</td>
                  </tr>
                );
              })}
          </table>
        </div>

        {/* <Flex className="zone-index-section" vertical={true} gap={4}>
          <Flex
            onClick={showModal}
            className="index-type"
            justify="space-between"
            align="center"
            data-testid="info-icon"
          >
            <div className="text-md text-semibold">
              {formatTranslation(t("autozones.drawer.index.title"))}
            </div>
            <Flex className="text-xs info-text" align="center">
              <span>
                <img src={infoIconSmall} alt="info icon" />
              </span>
              {formatTranslation(t("autozones.drawer.index.info"))}
            </Flex>
          </Flex>

          <Flex className="index-item" justify="space-between" gap={4}>
            <div className="text-md">
              {formatTranslation(t("autozones.drawer.index.variability"))}
            </div>
            <Flex align="center" gap={4}>
              <ZoneVarLevelTag value={variabilityData?.vxc} />
              <span className="index-value">
                {variabilityData?.vxc.toFixed(2)}
              </span>
            </Flex>
          </Flex>

          <Flex className="index-item" justify="space-between">
            <div className="text-md">
              {formatTranslation(t("autozones.drawer.index.heterogeneity"))}
            </div>
            <Flex align="center" gap={4}>
              <ZoneVarLevelTag value={variabilityData?.variability} />

              <span className="index-value">
                {variabilityData?.variability.toFixed(2)}
              </span>
            </Flex>
          </Flex>

          <Flex className="index-item" justify="space-between">
            <div className="text-md">
              {formatTranslation(t("autozones.drawer.index.consistency"))}
            </div>
            <Flex align="center" gap={4}>
              <ZoneVarLevelTag value={variabilityData?.consistency} />

              <span className="index-value">
                {variabilityData?.consistency.toFixed(2)}
              </span>
            </Flex>
          </Flex>
        </Flex> */}

        {/* <section className="zones-list-section">
          {data
            ?.slice() // create a shallow copy to avoid mutating original data
            .sort(
              (a, b) =>
                a.properties.percentiles.p50 - b.properties.percentiles.p50
            )
            .map((zone, index) => {
              const { zonePercentage = 0, zoneArea = 0 } =
                calculateZoneArea(selectedFieldData, zone) || {};

              return (
                <Flex className="zone-item" align="start" gap={8} key={index}>
                  <div
                    className="zone-color-circle"
                    style={{
                      backgroundColor:
                        autozoneColorPalette === AutozoneColorPalette.DEFAULT
                          ? getDefaultColorPaletteValue(index, data.length)
                          : autozoneColorPalette ===
                            AutozoneColorPalette.ACCESSIBILITY
                            ? getAccessibilityPaletteValue(index, data.length)
                            : getColorForNDVIValue(
                              zone.properties.percentiles.p50
                            ),
                    }}
                  ></div>
                  <Flex align="start" vertical={true} gap={2}>
                    <span className="text-md text-semibold">
                      Zone {index + 1}
                    </span>
                    <div className="text-sm zones-details">
                      {zone.properties.percentiles.p50.toFixed(2)} NDVI -{" "}
                      {zoneArea.toFixed(2)} ha - {zonePercentage.toFixed(2)}%{" "}
                      {formatTranslation(t("autozones.drawer.zone.oftotal"))}
                    </div>
                  </Flex>
                </Flex>
              );
            })}
        </section> */}
        <div style={{ width: "100%" }}>
          <div className="text-md text-semibold">
            {formatTranslation(t("autozones.drawer.colorPalette.title"))}
          </div>
          <Select
            value={autozoneColorPalette}
            style={{ width: "100%", marginTop: "2px" }}
            onChange={handleColorPaletteChange}
            options={AutozoneColorPaletteOptions}
          />
        </div>

        <section className="zones-opacity-section">
          <div className="opacity-title text-md">
            {formatTranslation(t("autozones.drawer.opacity.title"))}
          </div>
          <Slider
            value={autozoneOpacity}
            onChange={setAutozoneOpacity}
            style={{ margin: 0, marginTop: "12px" }}
            styles={{
              track: { backgroundColor: "#19A04B" },
              tracks: { backgroundColor: "#19A04B" },
            }}
          />
          <Flex justify="space-between" className="opacity-labels text-md">
            <div>0%</div>
            <div>100%</div>
          </Flex>
        </section>
      </div>
    </div>
  );
};

export default DrawerData;
